import React, { useEffect, useMemo, useState } from "react";
import { useFlashcardsStore } from "@/features/flashcards/store";
import type { FlashcardDeck, Flashcard } from "@/features/flashcards/types";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ChevronLeft, ChevronRight, WalletCards, SlidersHorizontal, Shuffle, Star, Trash2, Lightbulb, Pencil, Plus, Settings2, BookX, Sprout, Download, ChevronDown, ChevronUp, EllipsisVertical } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "@/hooks/use-toast";
import { StudyModeScreen } from "./StudyModeScreen";
import FlashcardFilterDialog from "./FlashcardFilterDialog";
import { useContentTopicsFromChapters } from "@/hooks/useContentChapters";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { ContentType } from "@/types/content";

type Props = {
  userId: string;
  contentId?: string | null;
  chatId?: string | null;
};

export const FlashcardsPanelContent: React.FC<Props> = ({
  userId,
  contentId = null,
  chatId = null
}) => {
  const {
    setContext,
    loadDecks,
    decks,
    selectedDeckId,
    selectDeck,
    deckTab,
    setDeckTab,
    isLoadingDecks,
    isLoadingCards,
    viewMode,
    cards,
    stats,
    generateDeckFromAI,
    isGenerating,
    addEmptyCard,
    deleteCardById,
    toggleStarById,
    updateCardById,
    setViewMode,
    deleteDeckById,
    updateDeckTitleById,
    isStudyMode,
    startStudyMode
  } = useFlashcardsStore();

  const [aiTopic, setAiTopic] = useState("");

  useEffect(() => {
    setContext({
      userId,
      contentId,
      chatId
    });
    loadDecks();
  }, [userId, contentId, chatId, setContext, loadDecks]);

  const selectedDeck = useMemo<FlashcardDeck | undefined>(
    () => decks.find((d) => d.id === selectedDeckId),
    [decks, selectedDeckId]
  );

  async function handleGenerateDeck() {
    if (!aiTopic.trim()) return;
    await generateDeckFromAI({
      topic: aiTopic.trim(),
      sourceText: null,
      numCards: 10
    });
    setAiTopic("");
  }

  // Show study mode screen when active
  if (isStudyMode) {
    return <StudyModeScreen />;
  }

  if (isLoadingDecks && viewMode === "list") {
    return <div className="p-4 text-sm text-muted-foreground">Loading decks…</div>;
  }

  if (viewMode === "list") {
    return (
      <DeckListView
        decks={decks}
        onDeckClick={(deckId) => selectDeck(deckId)}
        aiTopic={aiTopic}
        setAiTopic={setAiTopic}
        onGenerateDeck={handleGenerateDeck}
        isGenerating={isGenerating}
        onDeleteDeck={deleteDeckById}
        onRenameDeck={updateDeckTitleById}
        hideTopicGenerator={!!contentId} />);


  }

  if (!selectedDeck) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        No deck selected. Choose a deck from the list or generate a new one.
      </div>);

  }

  return (
    <div className="mx-auto flex h-full w-full max-w-3xl flex-col gap-2 pt-1">
      <div className="flex flex-col gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          className="flex h-fit w-fit items-center gap-2 px-3 py-1 pl-2 text-muted-foreground"
          onClick={() => selectDeck(null)}>

          <ChevronLeft className="h-4 w-4" />
          <span className="text-sm">Back</span>
        </Button>

        <Tabs
          value={deckTab}
          onValueChange={(v) => setDeckTab(v as "spaced" | "fast")}
          className="flex w-full flex-col items-center">

          <TabsList className="mb-0 flex h-10 w-fit items-center justify-center rounded-lg bg-primary/5 p-1 text-muted-foreground">
            <TabsTrigger value="spaced" className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm">
              Spaced Repetition
            </TabsTrigger>
            <TabsTrigger value="fast" className="rounded-lg px-3 py-1.5 text-sm">
              Fast Review
            </TabsTrigger>
          </TabsList>

          <TabsContent value="spaced" className="mt-2 w-full rounded-lg bg-background p-2">
            <SpacedRepetitionView
              deck={selectedDeck}
              cards={cards}
              stats={stats}
              isLoadingCards={isLoadingCards}
              onAddCard={addEmptyCard}
              onDeleteCard={deleteCardById}
              onToggleStar={toggleStarById}
              onUpdateCard={updateCardById}
              onStartStudy={startStudyMode} />

          </TabsContent>

          <TabsContent value="fast" className="mt-2 w-full rounded-lg bg-background p-2">
            <FastReviewView
              cards={cards}
              isLoadingCards={isLoadingCards}
              onToggleStar={toggleStarById}
              onManageCards={() => setDeckTab("spaced")}
              contentId={contentId} />

          </TabsContent>
        </Tabs>
      </div>
    </div>);

};
const DeckListView: React.FC<{
  decks: FlashcardDeck[];
  onDeckClick: (id: string) => void;
  aiTopic: string;
  setAiTopic: (v: string) => void;
  onGenerateDeck: () => void;
  isGenerating: boolean;
  onDeleteDeck: (id: string) => Promise<void>;
  onRenameDeck: (id: string, title: string) => Promise<void>;
  hideTopicGenerator?: boolean;
}> = ({
  decks,
  onDeckClick,
  aiTopic,
  setAiTopic,
  onGenerateDeck,
  isGenerating,
  onDeleteDeck,
  onRenameDeck,
  hideTopicGenerator = false
}) => {
  const [editingDeckId, setEditingDeckId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const handleStartEdit = (deck: FlashcardDeck, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingDeckId(deck.id);
    setEditTitle(deck.title);
  };
  const handleSaveEdit = async (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (!editingDeckId || !editTitle.trim()) return;
    await onRenameDeck(editingDeckId, editTitle.trim());
    toast({
      title: "Deck renamed successfully"
    });
    setEditingDeckId(null);
    setEditTitle("");
  };
  const handleCancelEdit = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setEditingDeckId(null);
    setEditTitle("");
  };
  const handleDelete = async (deckId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await onDeleteDeck(deckId);
    toast({
      title: "Deck deleted"
    });
  };
  return <div className="space-y-4">
      {/* Only show heading when decks exist */}
      {decks.length > 0 && <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-primary/80">My Flashcards</h3>
      </div>}

      {/* Quick AI deck generator - only show when no decks exist AND not in ContentPage */}
      {decks.length === 0 && !hideTopicGenerator && <Card className="border-dashed border-primary/20 bg-muted/40 p-3">
          <p className="mb-2 text-xs text-muted-foreground">
            Enter a topic to generate flashcards with AI
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input placeholder="e.g. Photosynthesis, Chapter 3, Derivatives..." value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} className="h-9 text-xs" onKeyDown={(e) => {
          if (e.key === "Enter" && aiTopic.trim()) {
            onGenerateDeck();
          }
        }} />
            <Button onClick={onGenerateDeck} disabled={!aiTopic.trim() || isGenerating} className="h-9 rounded-xl text-xs">
              {isGenerating ? "Generating…" : "Generate deck"}
            </Button>
          </div>
        </Card>}

      <div className="flex flex-col gap-3">
        {decks.length === 0 && !hideTopicGenerator && <Card className="rounded-2xl border border-primary/10 bg-card p-4 text-sm text-muted-foreground">
            No flashcard decks yet. Generate a deck from chat, or create one manually by topic.
          </Card>}

        {decks.map((deck) => <Card key={deck.id} className="group cursor-pointer rounded-2xl border border-primary/10 bg-card p-4 shadow-sm transition-all duration-200 hover:bg-accent/40" onClick={() => editingDeckId !== deck.id && onDeckClick(deck.id)}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex min-w-0 items-center gap-2 text-base font-medium">
                  {editingDeckId === deck.id ? <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveEdit(e);
                if (e.key === "Escape") handleCancelEdit(e);
              }} className="h-8 text-sm" autoFocus /> : <span className="truncate">{deck.title}</span>}
                </div>

                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded-full bg-purple-50 px-2 py-1 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200">
                    Cards for today: {deck.total_cards} cards
                  </span>
                  {deck.starred_cards > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-yellow-50 px-2 py-1 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-200">
                      <Star className="h-3 w-3 fill-yellow-400" />
                      {deck.starred_cards} starred
                    </span>}
                </div>
              </div>

              {editingDeckId === deck.id ? <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={handleSaveEdit}>Save</Button>
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={handleCancelEdit}>Cancel</Button>
                </div> : <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 transition-opacity group-hover:opacity-100">
                      <EllipsisVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="z-50 bg-popover">
                    <DropdownMenuItem onClick={(e) => handleStartEdit(deck, e)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => handleDelete(deck.id, e)} className="text-destructive focus:text-destructive">
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>}
            </div>
          </Card>)}
      </div>
    </div>;
};
const SpacedRepetitionView: React.FC<{
  deck: FlashcardDeck;
  cards: Flashcard[];
  stats: {
    notStudied: number;
    toReview: number;
    studiedToday: number;
  };
  isLoadingCards: boolean;
  onAddCard: () => void;
  onDeleteCard: (id: string) => void;
  onToggleStar: (id: string) => void;
  onUpdateCard: (id: string, fields: Partial<Pick<Flashcard, "term" | "definition" | "hint" | "explanation" | "source">>) => void;
  onStartStudy: () => void;
}> = ({
  deck,
  cards,
  stats,
  isLoadingCards,
  onAddCard,
  onDeleteCard,
  onToggleStar,
  onUpdateCard,
  onStartStudy
}) => {
  const total = cards.length || 1;
  const studiedPercent = (total - stats.notStudied) / total * 100;
  const dueCount = stats.notStudied + stats.toReview;

  return <div className="w-full space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-medium">Cards for today</h1>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-8 w-8 border-none text-muted-foreground">
            <Settings2 className="h-4 w-4" />
            <span className="sr-only">Flashcards preferences</span>
          </Button>
        </div>
      </div>

      <Card className="items-center justify-center rounded-2xl bg-muted/60 p-4 dark:bg-muted/40">
        <div className="flex flex-wrap items-center justify-center gap-6">
          <div className="relative flex h-24 w-24 items-center justify-center sm:h-32 sm:w-32">
            <div className="relative flex h-24 w-24 flex-col items-center justify-center sm:h-32 sm:w-32">
              <svg className="absolute left-0 top-0 h-full w-full" viewBox="0 0 100 100">
                <circle className="text-primary/5 dark:text-primary/10" strokeWidth="10" cx="50" cy="50" r="45" fill="transparent" stroke="currentColor" />
                <circle className="text-green-500" strokeWidth="10" cx="50" cy="50" r="45" fill="transparent" strokeDasharray="282.743" strokeDashoffset={282.743 - studiedPercent / 100 * 282.743} transform="rotate(-90 50 50)" stroke="currentColor" />
              </svg>
              <span className="z-10 text-3xl font-bold text-foreground sm:text-4xl">
                {total - stats.notStudied}
              </span>
            </div>
          </div>

          <div className="flex gap-6">
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-2 text-2xl font-semibold">
                <BookX className="h-4 w-4 text-muted-foreground/80" />
                {stats.notStudied}
              </div>
              <div className="mt-1 text-sm font-normal text-muted-foreground/80">
                Not Studied
              </div>
            </div>

            <div className="flex flex-col items-center">
              <div className="flex items-center gap-2 text-2xl font-semibold">
                <Sprout className="h-4 w-4 text-green-500" />
                {stats.toReview}
              </div>
              <div className="mt-1 text-sm font-normal text-muted-foreground/80">
                To review
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-center">
          <Button
          className="h-10 w-72 rounded-2xl"
          onClick={onStartStudy}
          disabled={dueCount === 0}>

            {dueCount > 0 ? `Study ${dueCount} Cards` : "All caught up!"}
          </Button>
        </div>
      </Card>

      <div>
        <h2 className="mb-4 mt-6 text-lg font-medium">Deck progress</h2>
        <Card className="rounded-2xl bg-muted/60 p-4 dark:bg-muted/40">
          <div className="mb-2 flex text-sm text-muted-foreground/80 gap-4">
            <div className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full bg-muted-foreground/50" />
              <span className="text-foreground">{stats.notStudied}</span>
              <span className="text-muted-foreground/80">Not Studied</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
              <span className="text-foreground">{stats.toReview}</span>
              <span className="text-muted-foreground/80">To review</span>
            </div>
          </div>

          <Progress value={studiedPercent} className="h-3" />
        </Card>
      </div>

      <div className="mt-4 flex items-center justify-between space-x-2">
        <div className="flex items-center">
          <span className="text-lg font-medium">
            Flashcards{" "}
            <span className="text-sm font-normal text-muted-foreground">
              ({cards.length})
            </span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-9 rounded-full border-2 border-dashed border-primary/20 bg-transparent text-sm font-medium" onClick={onAddCard}>
            <Plus className="mr-1 h-4 w-4" />
            Add Card
          </Button>
          <Button variant="ghost" size="icon" className="h-9 w-9">
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Card className="mt-4 flex h-full w-full flex-col p-4 bg-inherit border-none shadow-none">
        <div className="relative max-h-[calc(100vh-500px)] overflow-y-auto pb-3 scrollbar-hide">
          {isLoadingCards && <div className="p-3 text-xs text-muted-foreground">Loading cards…</div>}

          {!isLoadingCards && cards.length === 0 && <div className="p-3 text-xs text-muted-foreground">
              No cards yet. Generate from AI or add cards manually.
            </div>}

          <div className="space-y-3">
            {cards.map((card, index) => <FlashcardEditorRow key={card.id} card={card} index={index} onDelete={() => onDeleteCard(card.id)} onToggleStar={() => onToggleStar(card.id)} onUpdate={(fields) => onUpdateCard(card.id, fields)} />)}
          </div>
        </div>
      </Card>
    </div>;
};
const FlashcardEditorRow: React.FC<{
  card: Flashcard;
  index: number;
  onDelete: () => void;
  onToggleStar: () => void;
  onUpdate: (fields: Partial<Pick<Flashcard, "term" | "definition" | "hint" | "explanation" | "source">>) => void;
}> = ({
  card,
  index,
  onDelete,
  onToggleStar,
  onUpdate
}) => {
  const [showMore, setShowMore] = useState(false);
  return <Card className="relative rounded-lg border bg-card p-4 shadow-sm transition-all duration-200">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <p className="text-sm text-muted-foreground">Card {index + 1}</p>
            <Button variant="ghost" size="icon" className={cn("h-7 w-7 bg-transparent", card.is_starred && "text-yellow-500")} onClick={onToggleStar}>
              <Star className={cn("h-4 w-4", card.is_starred && "fill-yellow-400")} />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive" onClick={onDelete}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div>
          <p className="mb-1 text-sm font-medium">
            Term <span className="text-destructive">*</span>
          </p>
          <Textarea placeholder="Enter term…" className="min-h-[60px] resize-none text-sm" value={card.term} onChange={(e) => onUpdate({
          term: e.target.value
        })} />
        </div>

        <div>
          <p className="mb-1 text-sm font-medium">
            Definition <span className="text-destructive">*</span>
          </p>
          <Textarea placeholder="Enter definition…" className="min-h-[60px] resize-none text-sm" value={card.definition} onChange={(e) => onUpdate({
          definition: e.target.value
        })} />
        </div>

        {showMore && <>
            <div>
              <p className="mb-1 text-sm font-medium">Hint:</p>
              <Textarea placeholder="Short hint to jog your memory…" className="min-h-[50px] resize-none text-sm" value={card.hint} onChange={(e) => onUpdate({
            hint: e.target.value
          })} />
            </div>
            <div>
              <p className="mb-1 text-sm font-medium">Explanation:</p>
              <Textarea placeholder="Deeper explanation or context…" className="min-h-[50px] resize-none text-sm" value={card.explanation} onChange={(e) => onUpdate({
            explanation: e.target.value
          })} />
            </div>
            <div>
              <p className="mb-1 text-sm font-medium">Source:</p>
              <Input placeholder="Enter page number..." className="text-sm" value={card.source} onChange={(e) => onUpdate({
            source: e.target.value
          })} />
            </div>
          </>}

        <Button variant="ghost" size="sm" className="h-auto p-0 text-xs text-muted-foreground hover:bg-transparent hover:text-foreground" onClick={() => setShowMore(!showMore)}>
          {showMore ? <>
              <ChevronUp className="mr-1 h-3 w-3" />
              Show less
            </> : <>
              <ChevronDown className="mr-1 h-3 w-3" />
              Show more options
            </>}
        </Button>
      </div>
    </Card>;
};
const FastReviewView: React.FC<{
  cards: Flashcard[];
  isLoadingCards: boolean;
  onToggleStar: (id: string) => void;
  onManageCards: () => void;
  contentId?: string | null;
}> = ({
  cards,
  isLoadingCards,
  onToggleStar,
  onManageCards,
  contentId
}) => {
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterStarredOnly, setFilterStarredOnly] = useState(false);
  const [filterSelectedConcepts, setFilterSelectedConcepts] = useState<string[]>([]);

  // Fetch content type for this content
  const { data: contentType } = useQuery({
    queryKey: ["content-type", contentId],
    queryFn: async () => {
      if (!contentId) return null;
      const { data } = await supabase
        .from("content")
        .select("type")
        .eq("id", contentId)
        .single();
      return (data?.type as ContentType) ?? null;
    },
    enabled: !!contentId,
  });

  // Get chapter topics for filtering
  const { topics: availableTopics } = useContentTopicsFromChapters(
    contentId ?? undefined,
    contentType ?? undefined
  );

  const filteredCards = React.useMemo(() => {
    let result = cards;
    if (filterStarredOnly) {
      result = result.filter((c) => c.is_starred);
    }
    if (filterSelectedConcepts.length > 0) {
      result = result.filter((c) =>
        filterSelectedConcepts.some(
          (topic) =>
            c.term.toLowerCase().includes(topic.toLowerCase()) ||
            c.definition.toLowerCase().includes(topic.toLowerCase())
        )
      );
    }
    return result;
  }, [cards, filterStarredOnly, filterSelectedConcepts]);

  const card = filteredCards[index];
  function goNext() {
    setFlipped(false);
    setShowHint(false);
    setShowExplanation(false);
    setIndex((prev) => prev + 1 < filteredCards.length ? prev + 1 : prev);
  }
  function goPrev() {
    setFlipped(false);
    setShowHint(false);
    setShowExplanation(false);
    setIndex((prev) => prev - 1 >= 0 ? prev - 1 : prev);
  }
  function shuffle() {
    setIndex(Math.floor(Math.random() * filteredCards.length));
    setFlipped(false);
    setShowHint(false);
    setShowExplanation(false);
  }
  if (isLoadingCards) {
    return <div className="p-4 text-sm text-muted-foreground">Loading cards…</div>;
  }
  if (!card) {
    return <div className="p-4 text-sm text-muted-foreground">
        No cards in this deck yet. Add cards or generate them from AI.
      </div>;
  }
  return <div className="mt-2 w-full justify-center">
      <div className="w-full">
        <div className="relative w-full">
          <div className="w-full" role="region" aria-roledescription="carousel">
            <div className="w-full">
              <div className="perspective-1000">
                <div className={cn("relative aspect-[4/3] w-full cursor-pointer rounded-lg border text-card-foreground shadow-sm transition-all duration-500 preserve-3d bg-popover", flipped && "rotate-y-180")} onClick={() => setFlipped((f) => !f)}>
                  {/* Front */}
                  <div className="backface-hidden absolute inset-0 flex h-full flex-col overflow-y-auto p-4 text-center bg-popover rounded-2xl">
                    <div className="flex items-start justify-between">
                      <div>
                        <Button variant="ghost" size="sm" className="h-9 px-3 text-xs" onClick={(e) => {
                        e.stopPropagation();
                        setShowHint(!showHint);
                      }}>
                          <Lightbulb className="mr-1 h-4 w-4" />
                          <span className="hidden text-sm font-normal lg:block">Hint</span>
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" className={cn("h-9 w-9", card.is_starred && "text-yellow-500")} onClick={(e) => {
                        e.stopPropagation();
                        onToggleStar(card.id);
                      }}>
                          <Star className={cn("h-4 w-4", card.is_starred && "fill-yellow-400")} />
                        </Button>
                      </div>
                    </div>

                    <div className="flex flex-grow flex-col items-center justify-center px-2">
                      <p className="max-w-xl text-base leading-7 lg:text-lg">
                        {card.term || "Empty term"}
                      </p>
                      {showHint && card.hint && <p className="mt-4 text-sm text-muted-foreground italic">
                          Hint: {card.hint}
                        </p>}
                    </div>
                  </div>

                  {/* Back */}
                  <div className="backface-hidden rotate-y-180 absolute inset-0 flex h-full flex-col p-4 text-center">
                    <div className="flex items-start justify-end">
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" className={cn("h-9 w-9", card.is_starred && "text-yellow-500")} onClick={(e) => {
                        e.stopPropagation();
                        onToggleStar(card.id);
                      }}>
                          <Star className={cn("h-4 w-4", card.is_starred && "fill-yellow-400")} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={(e) => e.stopPropagation()}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="flex flex-grow flex-col items-center justify-center overflow-y-auto px-2">
                      <p className="max-w-xl text-base leading-7 lg:text-lg">
                        {card.definition || "Empty definition"}
                      </p>
                      {card.explanation &&
                    <>
                          <Button variant="ghost" size="sm" className="mt-3 h-8 px-3 text-xs" onClick={(e) => {e.stopPropagation();setShowExplanation((prev) => !prev);}}>
                            {showExplanation ? "Hide explanation" : "Show explanation"}
                          </Button>
                          {showExplanation &&
                      <p className="mt-2 max-w-xl text-sm text-muted-foreground leading-6">{card.explanation}</p>
                      }
                        </>
                    }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom controls */}
          <div className="mt-6 flex flex-col space-y-4">
            <div className="relative flex w-full items-center justify-center">
              <div className="absolute left-0">
                <Button variant="outline" size="icon" className="h-10 w-10" disabled={index === 0} onClick={goPrev} aria-label="Previous card">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex-shrink-0 text-sm text-muted-foreground">
                {index + 1} / {filteredCards.length}
              </div>
              <div className="absolute right-0">
                <Button variant="outline" size="icon" className="h-10 w-10" disabled={index === filteredCards.length - 1} onClick={goNext} aria-label="Next card">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button variant="ghost" size="sm" className="flex h-9 items-center gap-2 px-3" onClick={onManageCards}>
                <WalletCards className="h-4 w-4" />
                <span className="text-sm capitalize">Manage Cards</span>
              </Button>

              <div className="h-6 w-px bg-border" />

              <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => setFilterOpen(true)}>
                <SlidersHorizontal className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-10 w-10" onClick={shuffle}>
                <Shuffle className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <FlashcardFilterDialog
        open={filterOpen}
        onOpenChange={setFilterOpen}
        starredOnly={filterStarredOnly}
        selectedConcepts={filterSelectedConcepts}
        availableTopics={availableTopics}
        onApply={(starred, concepts) => {
          setFilterStarredOnly(starred);
          setFilterSelectedConcepts(concepts);
          setIndex(0);
          setFlipped(false);
        }}
      />
    </div>;
};